import {useConfig} from '@aboutyou/config';
import {StorySection as StorySectionType} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation_page/sections.types';
import {
  originClick,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk';
import {TrackPromotions, useTrackOriginClick} from '@aboutyou/tracking';
import {
  FONT_SIZES,
  HorizontalSlider,
  HorizontalSliderHeader,
  Image,
  mobileOnlyLinaria,
  RowSection,
  SliderPaginationBase,
  tabletAndUpLinaria,
  Text,
  UnstyledLinkLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {TextBanner} from '../../../../features/Recommendations/deprecated/shared';

export const StorySection: React.FC<
  StorySectionType & {streamPosition: number}
> = ({streamPosition, headline, caption, elements}) => {
  const {device} = useConfig();
  const trackOriginClick = useTrackOriginClick();

  const onStoryClick = (locationId?: string) => {
    if (locationId) {
      trackOriginClick(locationId as originClick)();
    }
  };

  return (
    <RowSection>
      <StyledHorizontalSlider
        dataTestId="storySection"
        hideSideButtons
        constrained={device === 'desktop'}
        disableHeaderControl={device === 'mobile'}
        SlideControlComponent={SliderPaginationBase}
        title={<StyledTextBanner headline={headline} subline={caption} />}
        slideCount={1.5}
        slideCountSmall={2.5}
        slideCountMedium={4}
        slideCountLarge={4}
        uniqueKey={`storySection-${streamPosition}`}
        enableGridLayout
      >
        {elements.map((element, index) => (
          <TrackPromotions
            key={index}
            Comp="li"
            streamPosition={streamPosition}
            promotion={{
              source: TrackingPromotionCreative.GenericPromotion,
              promotionTracker: {
                ...element.promotionTracker,
                destination: element.link.url,
              },
            }}
            horizontalPosition={index}
            triggerOnce
          >
            <StoryCard
              data-testid="storySectionCard"
              to={element.link.url}
              aria-label={element.subline}
              onClick={() => onStoryClick(element.promotionTracker.locationId)}
              considerScrollTopAnchor
            >
              <StyledImage
                src={element.coverImage.url}
                alt={element.headline ?? element.subline}
                breakpoints={[]}
                ratio="3:4"
              />
              <StoryCardLabel>
                {element.headline && (
                  <Headline
                    size="px12"
                    weight="mediumHeavy"
                    transform="uppercase"
                  >
                    {element.headline}
                  </Headline>
                )}
                <Subline size="px16" weight="mediumBold">
                  {element.subline}
                </Subline>
              </StoryCardLabel>
            </StoryCard>
          </TrackPromotions>
        ))}
      </StyledHorizontalSlider>
    </RowSection>
  );
};

const StyledTextBanner = styled(TextBanner)`
  ${mobileOnlyLinaria(`margin-left: 16px;`)}
`;

const StyledHorizontalSlider = styled(HorizontalSlider)`
  ul {
    gap: 3px;
  }

  ${tabletAndUpLinaria(`
    ul {
      padding-left: 0;
      margin-top: 24px;
      gap: unset;
    }
  `)}

  ${HorizontalSliderHeader} {
    margin: 0;
  }
`;

const StoryCard = styled(UnstyledLinkLinaria)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledImage = styled(Image)`
  border-radius: 8px;
  flex-shrink: 0;
  width: 100%;
  height: auto;
  object-fit: cover;
`;

const StoryCardLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Headline = styled(Text)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  font-size: ${FONT_SIZES.px12};

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px14};
  `)}
`;

const Subline = styled(Text)`
  font-size: ${FONT_SIZES.px14};

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px16};
  `)}
`;
